import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Phone, MapPin, AlertTriangle, ChevronDown, Search } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from '../components/ui/accordion';
import { Input } from '../components/ui/input';
import { getEmergencyHotlines } from '../lib/api';
import { useLanguage } from '../context/LanguageContext';

const FALLBACK_HOTLINES = [
  { id: 1, region: 'Nationwide', label: 'Medical Emergency',       phoneNumber: '115',       description: '24/7 national medical emergency ambulance service' },
  { id: 2, region: 'Nationwide', label: 'Police',                  phoneNumber: '113',       description: 'Vietnam police emergency hotline' },
  { id: 3, region: 'Nationwide', label: 'Fire Department',         phoneNumber: '114',       description: 'Fire and rescue emergency services' },
  { id: 4, region: 'Nationwide', label: 'Ministry of Health',      phoneNumber: '1900 9095', description: 'Health information and complaints' },
];

const EmergencyHotlinesPage = () => {
  const { t } = useLanguage();
  const [hotlines, setHotlines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    getEmergencyHotlines()
      .then((res) => {
        if (res.data?.success && Array.isArray(res.data.data) && res.data.data.length > 0) {
          setHotlines(res.data.data.filter((h) => h.isActive !== false));
        } else {
          setHotlines(FALLBACK_HOTLINES);
        }
      })
      .catch(() => setHotlines(FALLBACK_HOTLINES))
      .finally(() => setLoading(false));
  }, []);

  // Group by region
  const grouped = useMemo(() => {
    const q = searchQuery.toLowerCase();
    const filtered = q
      ? hotlines.filter(
          (h) =>
            h.region.toLowerCase().includes(q) ||
            h.label.toLowerCase().includes(q) ||
            (h.description || '').toLowerCase().includes(q) ||
            h.phoneNumber.includes(q)
        )
      : hotlines;

    return filtered.reduce((acc, h) => {
      if (!acc[h.region]) acc[h.region] = [];
      acc[h.region].push(h);
      return acc;
    }, {});
  }, [hotlines, searchQuery]);

  const regions = Object.keys(grouped);
  const totalCount = hotlines.length;

  // Nationwide expanded by default; others collapsed
  const defaultOpen = searchQuery ? regions : regions.filter((r) => r === 'Nationwide');

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Helmet>
        <title>Emergency Hotlines in Vietnam — Medical, Police & Fire | VietClinics</title>
        <meta name="description" content="Vietnam emergency hotlines for tourists, expats and residents — 115 ambulance, 113 police, 114 fire and regional medical emergency contacts." />
        <link rel="canonical" href="https://vietclinics.com/emergency" />
      </Helmet>
      <Header />

      <main className="flex-1">
        {/* ── Red alert banner ──────────────────────────────────────────────── */}
        <div className="bg-red-600 text-white py-4 px-4">
          <div className="container mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 flex-shrink-0 animate-pulse" />
              <span className="font-semibold text-sm sm:text-base">
                For life-threatening emergencies, always call <strong>115</strong> immediately
              </span>
            </div>
            <a
              href="tel:115"
              className="flex items-center gap-2 bg-white text-red-600 font-bold px-5 py-2 rounded-full text-sm hover:bg-red-50 transition-colors flex-shrink-0"
              data-testid="call-115-banner-btn"
            >
              <Phone className="h-4 w-4" />
              Call 115 Now
            </a>
          </div>
        </div>

        {/* ── Page header ──────────────────────────────────────────────────── */}
        <div className="bg-white border-b border-gray-100">
          <div className="container mx-auto px-4 py-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-1">Emergency Hotlines</h1>
                <p className="text-gray-500 text-sm">
                  {loading
                    ? 'Loading...'
                    : t('emergency.hotlineCount', { count: totalCount }).replace('{count}', totalCount)}
                </p>
              </div>
              {/* Search */}
              <div className="relative w-full sm:w-72">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder={t('emergency.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 h-10"
                  data-testid="hotline-search-input"
                />
              </div>
            </div>
          </div>
        </div>

        {/* ── Hotlines accordion ─────────────────────────────────────────────── */}
        <div className="container mx-auto px-4 py-8 max-w-3xl">
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white rounded-2xl h-16 animate-pulse border border-gray-100" />
              ))}
            </div>
          ) : regions.length === 0 ? (
            <div className="bg-white rounded-2xl p-10 text-center">
              <Search className="h-10 w-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No hotlines found for &ldquo;{searchQuery}&rdquo;</p>
            </div>
          ) : (
            <Accordion
              type="multiple"
              defaultValue={defaultOpen}
              className="space-y-3"
              data-testid="hotlines-accordion"
            >
              {regions.map((region) => (
                <AccordionItem
                  key={region}
                  value={region}
                  className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden px-5"
                >
                  <AccordionTrigger
                    className="hover:no-underline py-4 text-base"
                    data-testid={`region-${region.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    <div className="flex items-center gap-2">
                      <MapPin className={`h-4 w-4 flex-shrink-0 ${region === 'Nationwide' ? 'text-red-600' : 'text-[#1FA7A1]'}`} />
                      <span className="font-semibold text-gray-900">{region}</span>
                      <span className="ml-2 text-xs font-medium bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">
                        {grouped[region].length}
                      </span>
                    </div>
                  </AccordionTrigger>

                  <AccordionContent>
                    <div className="divide-y divide-gray-50 -mx-5 px-5">
                      {grouped[region]
                        .sort((a, b) => (a.sortOrder || 99) - (b.sortOrder || 99))
                        .map((hotline) => (
                          <div
                            key={hotline.id}
                            className="py-3.5 flex items-start justify-between gap-4"
                            data-testid={`hotline-row-${hotline.id}`}
                          >
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-gray-900 text-sm">{hotline.label}</p>
                              {hotline.description && (
                                <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">{hotline.description}</p>
                              )}
                            </div>
                            <a
                              href={`tel:${hotline.phoneNumber.replace(/\s/g, '')}`}
                              className="flex items-center gap-1.5 bg-red-50 text-red-600 font-bold text-sm px-3 py-1.5 rounded-xl hover:bg-red-100 transition-colors flex-shrink-0 whitespace-nowrap"
                              data-testid={`call-btn-${hotline.id}`}
                            >
                              <Phone className="h-3.5 w-3.5" />
                              {hotline.phoneNumber}
                            </a>
                          </div>
                        ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}

          {/* Disclaimer */}
          <p className="text-center text-xs text-gray-400 mt-8 leading-relaxed">
            Hotline information is provided for guidance only. In an emergency, always call
            <strong> 115</strong> first. VietClinics is not responsible for the availability
            of third-party services.
          </p>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default EmergencyHotlinesPage;
